export const PASSWORD_MIN_LENGTH = 12;

export interface PasswordRule {
  readonly id: string;
  /** Shown in the live checklist next to a password field. */
  readonly label: string;
  /** Used verbatim as the server-side rejection message. */
  readonly message: string;
  readonly test: (password: string) => boolean;
}

/**
 * The one password policy for client accounts.
 *
 * Browser-safe so every password field validates against exactly what the
 * server enforces. Previously there were three copies — the server validator,
 * the client portal's checklist and the admin form's `minLength(8)` — and they
 * disagreed: the portal accepted any non-alphanumeric as a special character
 * while the server required one from the explicit set below, so a password
 * could show all-green and still be rejected.
 */
export const PASSWORD_RULES: ReadonlyArray<PasswordRule> = [
  {
    id: 'length',
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`,
    test: (p) => p.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: 'lower',
    label: '1 lowercase letter',
    message: 'Password must contain at least one lowercase letter',
    test: (p) => /[a-z]/.test(p),
  },
  {
    id: 'upper',
    label: '1 uppercase letter',
    message: 'Password must contain at least one uppercase letter',
    test: (p) => /[A-Z]/.test(p),
  },
  {
    id: 'number',
    label: '1 number',
    message: 'Password must contain at least one number',
    test: (p) => /\d/.test(p),
  },
  {
    id: 'special',
    label: '1 special character (!@#$%^&*…)',
    message: 'Password must contain at least one special character',
    test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(p),
  },
];

/** The first unmet rule, or null when the password satisfies all of them. */
export function firstFailedPasswordRule(password: string): PasswordRule | null {
  return PASSWORD_RULES.find((rule) => !rule.test(password)) ?? null;
}

export function isPasswordStrong(password: string): boolean {
  return firstFailedPasswordRule(password) === null;
}
